import { useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface ExistingOrderFormProps {
  userEmail: string;
  userName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ExistingOrderForm({ userEmail, userName, onClose, onSuccess }: ExistingOrderFormProps) {
  const [formData, setFormData] = useState({
    orderTitle: '',
    orderDescription: '',
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setUploading(true);

    try {
      const { data: orderData, error: insertError } = await supabase
        .from('orders')
        .insert({
          customer_email: userEmail,
          customer_name: userName,
          order_title: formData.orderTitle,
          order_description: formData.orderDescription,
          file_urls: [],
          status: 'pending',
        })
        .select()
        .single();

      if (insertError) throw insertError;

      if (orderData) {
        const { error: cartError } = await supabase.from('cart').insert({
          customer_email: userEmail,
          order_id: orderData.id,
          order_title: formData.orderTitle,
          order_description: formData.orderDescription,
          file_urls: [],
        });

        if (cartError) {
          console.error('Sepete eklenirken hata:', cartError);
        }
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Sipariş oluşturulurken bir hata oluştu');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl lg:rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 lg:px-6 py-3 lg:py-4 flex items-center justify-between rounded-t-xl lg:rounded-t-2xl">
          <h3 className="text-lg lg:text-xl font-semibold text-gray-900">Mevcut Sipariş Takibi</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line text-xl lg:text-2xl text-gray-500"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 lg:p-6 space-y-4 lg:space-y-5 bg-white">
          {/* Bilgilendirme Mesajı */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-amber-600 text-xl mt-0.5 flex-shrink-0"></i>
              <p className="text-amber-800 text-sm leading-relaxed">
                Mevcut siparişinizin takibi, işlemi başlattıktan sonra aktif hâle gelmesi için yaklaşık 1 saat sürebilir.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">
              Firma Adı <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <i className="ri-building-2-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={formData.orderTitle}
                onChange={(e) => setFormData({ ...formData, orderTitle: e.target.value })}
                className="w-full bg-gray-50 border border-gray-300 rounded-lg pl-10 pr-4 py-2.5 lg:py-3 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none text-sm"
                placeholder="Örn: ABC Şirketi"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">Açıklama</label>
            <textarea
              value={formData.orderDescription}
              onChange={(e) => setFormData({ ...formData, orderDescription: e.target.value })}
              rows={4}
              maxLength={500}
              className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 lg:py-3 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none text-sm resize-none"
              placeholder="Mevcut siparişiniz hakkında detaylı bilgi verin..."
            />
            <p className="text-gray-500 text-xs mt-1 text-right">{formData.orderDescription.length}/500</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 lg:px-4 py-2.5 lg:py-3 rounded-lg text-sm flex items-center gap-2">
              <i className="ri-error-warning-line flex-shrink-0"></i>
              <span>{error}</span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 lg:px-6 py-2.5 lg:py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer text-sm lg:text-base"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="flex-1 px-4 lg:px-6 py-2.5 lg:py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-medium hover:from-green-600 hover:to-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-sm lg:text-base"
            >
              {uploading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Oluşturuluyor...
                </span>
              ) : (
                'Sipariş Takibini Başlat'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
